import { useApolloClient } from "@apollo/client";
import { CardContainer, LabeledValue } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as Yup from "yup";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ManualSave } from "../../../commons/form-fields/ManualSave.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  AccountingBox__OrganisationCountersFragment,
  AccountingBox__OrganisationSettingsFragment,
  useOrganisationPaymentAccountingBox_ModifyOrganisationCountersMutation,
  useOrganisationPaymentAccountingBox_ModifyOrganisationSettingsMutation,
} from "./OrganisationPaymentAccountingBox.generated.js";

type Entity =
  | "invoice"
  | "quote"
  | "requirement"
  | "timeReport"
  | "purchaseOrder"
  | "crmOrganisation"
  | "crmUser";

const makeNextNumber = (
  prefix: string,
  minLength: number,
  nextCounter: number
): string => {
  const now = moment();
  return (
    prefix
      .replace(/<YYYY>/gi, now.format("YYYY"))
      .replace(/<YY>/gi, now.format("YY"))
      .replace(/<MM>/gi, now.format("MM"))
      .replace(/<DD>/gi, now.format("DD")) +
    Math.max(nextCounter, 1).toString().padStart(Math.max(minLength, 1), "0")
  );
};

interface FormValues {
  invoiceCounter: number;
  invoiceNumberMinLength: number;
  invoiceNumberPrefix: string;

  timeReportCounter: number;
  timeReportNumberMinLength: number;
  timeReportNumberPrefix: string;

  purchaseOrderCounter: number;
  purchaseOrderNumberMinLength: number;
  purchaseOrderNumberPrefix: string;

  crmOrganisationCounter: number;
  crmOrganisationNumberMinLength: number;
  crmOrganisationNumberPrefix: string;

  crmUserCounter: number;
  crmUserNumberMinLength: number;
  crmUserNumberPrefix: string;

  quoteCounter: number;
  quoteNumberMinLength: number;
  quoteNumberPrefix: string;

  requirementCounter: number;
  requirementNumberMinLength: number;
  requirementNumberPrefix: string;
}

interface Props {
  organisationId: string;
  organisationSettings: AccountingBox__OrganisationSettingsFragment;
  organisationCounters: AccountingBox__OrganisationCountersFragment;
}

export const OrganisationPaymentAccountingBox = ({
  organisationId,
  organisationSettings,
  organisationCounters,
}: Props) => {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const [isEditing, setIsEditing] = useState(false);

  if (viewer.organisation.id !== organisationId)
    return <div>no permission to access organisationSettings</div>;

  return (
    <CardContainer
      title={t("Accounting settings")}
      ActionButton={
        !isEditing && (
          <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
            <IconButton
              color="primary"
              size="small"
              onClick={() => setIsEditing(true)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </RestrictedByOrganisationPermissionWithDebug>
        )
      }
    >
      <Box
        sx={{
          p: 1,
        }}
      >
        {isEditing ? (
          <AccountingSettingsForm
            organisationSettings={organisationSettings}
            organisationCounters={organisationCounters}
            onDone={() => setIsEditing(false)}
          />
        ) : (
          <AccountingSettingsData
            organisationSettings={organisationSettings}
            organisationCounters={organisationCounters}
          />
        )}
      </Box>
    </CardContainer>
  );
};

function AccountingSettingsData({
  organisationSettings,
  organisationCounters,
}: {
  organisationSettings: AccountingBox__OrganisationSettingsFragment;
  organisationCounters: AccountingBox__OrganisationCountersFragment;
}) {
  const { t } = useTranslate("OrganisationSettings");
  return (
    <Stack spacing={1} direction="column">
      <RestrictedByCapabilityWithDebug capability="QUOTING">
        <LabeledValue label={t("Quote number")}>
          {makeNextNumber(
            organisationSettings.quoteNumberPrefix,
            organisationSettings.quoteNumberMinLength,
            organisationCounters.quoteCounter
          )}
        </LabeledValue>
      </RestrictedByCapabilityWithDebug>

      <RestrictedByCapabilityWithDebug capability="INVOICING">
        <LabeledValue label={t("Invoice number")}>
          {makeNextNumber(
            organisationSettings.invoiceNumberPrefix,
            organisationSettings.invoiceNumberMinLength,
            organisationCounters.invoiceCounter
          )}
        </LabeledValue>
      </RestrictedByCapabilityWithDebug>

      <RestrictedByCapabilityWithDebug capability="REQUIREMENTS">
        <LabeledValue label={t("Requirement number")}>
          {makeNextNumber(
            organisationSettings.requirementNumberPrefix,
            organisationSettings.requirementNumberMinLength,
            organisationCounters.requirementCounter
          )}
        </LabeledValue>
      </RestrictedByCapabilityWithDebug>

      <RestrictedByCapabilityWithDebug capability="ORDERING">
        <LabeledValue label={t("Purchase order number")}>
          {makeNextNumber(
            organisationSettings.purchaseOrderNumberPrefix,
            organisationSettings.purchaseOrderNumberMinLength,
            organisationCounters.purchaseOrderCounter
          )}
        </LabeledValue>
      </RestrictedByCapabilityWithDebug>

      <RestrictedByCapabilityWithDebug capability="TIME_TRACKING">
        <LabeledValue label={t("Time report number")}>
          {makeNextNumber(
            organisationSettings.timeReportNumberPrefix,
            organisationSettings.timeReportNumberMinLength,
            organisationCounters.timeReportCounter
          )}
        </LabeledValue>
      </RestrictedByCapabilityWithDebug>

      <LabeledValue label={t("Client number")}>
        {makeNextNumber(
          organisationSettings.crmOrganisationNumberPrefix,
          organisationSettings.crmOrganisationNumberMinLength,
          organisationCounters.crmOrganisationCounter
        )}
      </LabeledValue>

      <LabeledValue label={t("Contact number")}>
        {makeNextNumber(
          organisationSettings.crmUserNumberPrefix,
          organisationSettings.crmUserNumberMinLength,
          organisationCounters.crmUserCounter
        )}
      </LabeledValue>
    </Stack>
  );
}

const validationSchema = Yup.object().shape({
  invoiceCounter: Yup.number().required(),
  invoiceNumberMinLength: Yup.number().required(),
  invoiceNumberPrefix: Yup.string(),

  timeReportCounter: Yup.number().required(),
  timeReportNumberMinLength: Yup.number().required(),
  timeReportNumberPrefix: Yup.string(),

  purchaseOrderCounter: Yup.number().required(),
  purchaseOrderNumberMinLength: Yup.number().required(),
  purchaseOrderNumberPrefix: Yup.string(),

  crmOrganisationCounter: Yup.number().required(),
  crmOrganisationNumberMinLength: Yup.number().required(),
  crmOrganisationNumberPrefix: Yup.string(),

  crmUserCounter: Yup.number().required(),
  crmUserNumberMinLength: Yup.number().required(),
  crmUserNumberPrefix: Yup.string(),

  quoteCounter: Yup.number().required(),
  quoteNumberMinLength: Yup.number().required(),
  quoteNumberPrefix: Yup.string(),

  requirementCounter: Yup.number().required(),
  requirementNumberMinLength: Yup.number().required(),
  requirementNumberPrefix: Yup.string(),
});

function AccountingSettingsForm({
  organisationSettings,
  organisationCounters,
  onDone,
}: {
  organisationSettings: AccountingBox__OrganisationSettingsFragment;
  organisationCounters: AccountingBox__OrganisationCountersFragment;
  onDone: () => void;
}) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [modifyOrganisationSettings] =
    useOrganisationPaymentAccountingBox_ModifyOrganisationSettingsMutation({
      client,
    });
  const [modifyOrganisationCounters] =
    useOrganisationPaymentAccountingBox_ModifyOrganisationCountersMutation({
      client,
    });

  const initialValues: FormValues = {
    invoiceCounter: organisationCounters.invoiceCounter,
    invoiceNumberMinLength: organisationSettings.invoiceNumberMinLength,
    invoiceNumberPrefix: organisationSettings.invoiceNumberPrefix,

    timeReportCounter: organisationCounters.timeReportCounter,
    timeReportNumberMinLength: organisationSettings.timeReportNumberMinLength,
    timeReportNumberPrefix: organisationSettings.timeReportNumberPrefix,

    purchaseOrderCounter: organisationCounters.purchaseOrderCounter,
    purchaseOrderNumberMinLength:
      organisationSettings.purchaseOrderNumberMinLength,
    purchaseOrderNumberPrefix: organisationSettings.purchaseOrderNumberPrefix,

    crmOrganisationCounter: organisationCounters.crmOrganisationCounter,
    crmOrganisationNumberMinLength:
      organisationSettings.crmOrganisationNumberMinLength,
    crmOrganisationNumberPrefix:
      organisationSettings.crmOrganisationNumberPrefix,

    crmUserCounter: organisationCounters.crmUserCounter,
    crmUserNumberMinLength: organisationSettings.crmUserNumberMinLength,
    crmUserNumberPrefix: organisationSettings.crmUserNumberPrefix,

    quoteCounter: organisationCounters.quoteCounter,
    quoteNumberMinLength: organisationSettings.quoteNumberMinLength,
    quoteNumberPrefix: organisationSettings.quoteNumberPrefix,

    requirementCounter: organisationCounters.requirementCounter,
    requirementNumberMinLength: organisationSettings.requirementNumberMinLength,
    requirementNumberPrefix: organisationSettings.requirementNumberPrefix,
  };

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        try {
          await modifyOrganisationSettings({
            variables: {
              input: {
                invoiceNumberMinLength: values.invoiceNumberMinLength,
                invoiceNumberPrefix: values.invoiceNumberPrefix,

                timeReportNumberMinLength: values.timeReportNumberMinLength,
                timeReportNumberPrefix: values.timeReportNumberPrefix,

                purchaseOrderNumberMinLength:
                  values.purchaseOrderNumberMinLength,
                purchaseOrderNumberPrefix: values.purchaseOrderNumberPrefix,

                crmOrganisationNumberMinLength:
                  values.crmOrganisationNumberMinLength,
                crmOrganisationNumberPrefix: values.crmOrganisationNumberPrefix,

                crmUserNumberMinLength: values.crmUserNumberMinLength,
                crmUserNumberPrefix: values.crmUserNumberPrefix,

                quoteNumberMinLength: values.quoteNumberMinLength,
                quoteNumberPrefix: values.quoteNumberPrefix,

                requirementNumberMinLength: values.requirementNumberMinLength,
                requirementNumberPrefix: values.requirementNumberPrefix,
              },
            },
          });
          await modifyOrganisationCounters({
            variables: {
              input: {
                invoiceCounter: values.invoiceCounter,
                timeReportCounter: values.timeReportCounter,
                purchaseOrderCounter: values.purchaseOrderCounter,
                crmOrganisationCounter: values.crmOrganisationCounter,
                crmUserCounter: values.crmUserCounter,
                quoteCounter: values.quoteCounter,
                requirementCounter: values.requirementCounter,
              },
            },
          });
          onDone();
        } catch (error) {
          if (error instanceof Error)
            enqueueSnackbar(error.message, { variant: "error" });
        }
      }}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <Stack direction="column" spacing={1.5}>
            <Stack direction="column" spacing={0}>
              <Typography color="textSecondary" variant="caption">
                {"<YYYY>"} – {t("current year")} ({t("long format")})
              </Typography>
              <Typography color="textSecondary" variant="caption">
                {"<YY>"} – {t("current year")} ({t("short format")})
              </Typography>
              <Typography color="textSecondary" variant="caption">
                {"<MM>"} – {t("current month")}
              </Typography>
              <Typography color="textSecondary" variant="caption">
                {"<DD>"} – {t("current day")}
              </Typography>
            </Stack>

            <RestrictedByCapabilityWithDebug capability="QUOTING">
              <AccountSettingsRow
                title={t("Quote number")}
                name="quote"
                values={values}
                disabled={isSubmitting}
              />
            </RestrictedByCapabilityWithDebug>

            <RestrictedByCapabilityWithDebug capability="INVOICING">
              <AccountSettingsRow
                title={t("Invoice number")}
                name="invoice"
                values={values}
                disabled={isSubmitting}
              />
            </RestrictedByCapabilityWithDebug>

            <RestrictedByCapabilityWithDebug capability="REQUIREMENTS">
              <AccountSettingsRow
                title={t("Requirement number")}
                name="requirement"
                values={values}
                disabled={isSubmitting}
              />
            </RestrictedByCapabilityWithDebug>

            <RestrictedByCapabilityWithDebug capability="ORDERING">
              <AccountSettingsRow
                title={t("Purchase order number")}
                name="purchaseOrder"
                values={values}
                disabled={isSubmitting}
              />
            </RestrictedByCapabilityWithDebug>

            <RestrictedByCapabilityWithDebug capability="TIME_TRACKING">
              <AccountSettingsRow
                title={t("Time report number")}
                name="timeReport"
                values={values}
                disabled={isSubmitting}
              />
            </RestrictedByCapabilityWithDebug>

            <AccountSettingsRow
              title={t("Client number")}
              name="crmOrganisation"
              values={values}
              disabled={isSubmitting}
            />

            <AccountSettingsRow
              title={t("Contact number")}
              name="crmUser"
              values={values}
              disabled={isSubmitting}
            />

            <ManualSave
              onCancel={onDone}
              buttonLayout="inline"
              saveButtonTitle={t("Save", { ns: "Global" })}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}

const AccountSettingsRow = ({
  title,
  name,
  values,
  disabled,
}: {
  title: string;
  name: Entity;
  values: FormValues;
  disabled: boolean;
}) => {
  const { t } = useTranslate("OrganisationSettings");
  return (
    <Box>
      <Typography>
        {title}:{" "}
        {makeNextNumber(
          values[`${name}NumberPrefix`],
          values[`${name}NumberMinLength`],
          values[`${name}Counter`]
        )}
      </Typography>
      <Grid container spacing={1}>
        <Grid size={6}>
          <TextField
            name={`${name}NumberPrefix`}
            label={t("Prefix")}
            disabled={disabled}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            type="number"
            name={`${name}NumberMinLength`}
            label={t("Min. length")}
            disabled={disabled}
            slotProps={{
              htmlInput: {
                min: 1,
                max: 30,
              },
            }}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            type="number"
            name={`${name}Counter`}
            label={t("Next value")}
            disabled={disabled}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
